<template>
  <div class="page-view">

    <!-- Top bar -->
    <BasePageHeader :title="project?.name ?? $t('projectPick.pickList')" @back="$router.back()">
      <template #actions>
        <RouterLink
          :to="`/project/${projectId}`"
          class="btn-icon"
          :title="$t('projectPick.goToProject')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
            <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
          </svg>
        </RouterLink>
        <RouterLink
          :to="`/project/${projectId}/items`"
          class="btn-icon"
          :title="$t('projectPick.goToPlannedItems')"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
            <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
            <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
          </svg>
        </RouterLink>
        <span v-if="project?.date" class="text-xs text-gray-500 shrink-0">{{ project.date }}</span>
      </template>
    </BasePageHeader>
    <BaseTaskHelp task="pick" class="mx-4 mt-3" />

    <!-- Draft warning -->
    <div v-if="project?.status === 0" class="flex items-center gap-2.5 px-4 py-2.5 bg-orange-950 border-b border-amber-800 text-amber-200 text-sm">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="shrink-0 w-4 h-4 text-amber-500">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <span class="flex-1">{{ $t('projectPick.draftWarningPre') }} <strong class="text-amber-400">{{ $t('projectPick.draft') }}</strong> {{ $t('projectPick.draftWarningPost') }}</span>
      <button class="shrink-0 bg-amber-900 border-0 rounded text-amber-200 text-xs font-semibold px-3 py-1 cursor-pointer hover:bg-amber-700 disabled:opacity-50 disabled:cursor-default" :disabled="confirmingProject" @click="confirmProject">
        {{ confirmingProject ? '…' : $t('common.confirm') }}
      </button>
    </div>

    <!-- Progress circle + summary -->
    <div class="flex items-center gap-4 p-4 px-5">
      <svg style="width:80px;height:80px;overflow:visible" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r="34" style="fill:none;stroke:#1f2937;stroke-width:7" />
        <circle
          cx="40" cy="40" r="34"
          :style="{
            fill: 'none',
            stroke: progressPercent === 100 ? '#16a34a' : '#3b82f6',
            strokeWidth: 7,
            strokeLinecap: 'round',
            transform: 'rotate(-90deg)',
            transformOrigin: '40px 40px',
            transition: 'stroke-dashoffset .4s ease',
          }"
          :stroke-dasharray="`${progressCircumference}`"
          :stroke-dashoffset="`${progressOffset}`"
        />
        <text x="40" y="44" text-anchor="middle" style="fill:#f9fafb;font-size:12px;font-weight:700;font-family:inherit">
          {{ packedCount }}/{{ totalCount }}
        </text>
      </svg>
      <div class="text-sm text-gray-400">
        <span v-if="progressPercent === 100" class="text-green-400 font-semibold">{{ $t('projectPick.allPacked') }}</span>
        <span v-else>{{ totalCount - packedCount }} {{ $t('projectPick.remaining') }}</span>
      </div>
    </div>

    <!-- Scan bar + live search dropdown -->
    <div class="px-4 pb-3">
      <SearchField
        ref="scanner"
        :scan-mode="true"
        :camera-mode="true"
        :results="mappedLiveResults"
        :loading="scanLoading || liveAdding || liveLoading"
        :error="scanError"
        :debounce="280"
        search-key="pick-items"
        :placeholder="$t('ui.scanBarcodeOrTypeNameToSearch')"
        @scan="onScan"
        @search="doLiveSearch"
        @select="addFromLive($event.meta)"
      />
    </div>

    <!-- Auto-add toggle -->
    <div class="px-4 pb-2 flex items-center justify-between gap-3 text-xs text-gray-500">
      <span>{{ $t('projectPick.autoAdd') }}</span>
      <BaseFormField density="compact" class="!mb-0 shrink-0">
        <BaseToggle
          v-model="autoAdd"
          :aria-label="$t('projectPick.autoAddAria')"
          density="compact"
        />
      </BaseFormField>
    </div>

    <!-- Ghost item prompt -->
    <div v-if="ghostItem" class="mx-4 mb-3 bg-[#1c1c0a] border border-amber-800 rounded-xl p-4">
      <div class="flex items-center gap-2 text-base text-amber-200 mb-3">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 shrink-0">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
        <span>"<strong>{{ ghostItem }}</strong>" {{ $t('projectPick.notOnPickList') }}</span>
      </div>
      <div class="flex gap-2">
        <button class="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border-0 bg-gray-700 text-gray-300" @click="ghostItem = null">{{ $t('common.dismiss') }}</button>
        <button class="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border-0 bg-emerald-900 text-emerald-200" @click="addAdhoc()">{{ $t('projectPick.addAndCheckOut') }}</button>
      </div>
    </div>

    <!-- Manual checkout modal (Type 1/2) -->
    <div v-if="manualTarget" class="fixed inset-0 bg-black/65 flex items-center justify-center z-[150] p-6" @click.self="manualTarget = null">
      <div class="bg-[var(--color-surface)] border border-gray-700 rounded-2xl p-6 w-full max-w-sm">
        <div class="text-base font-bold mb-3.5">{{ $t('projectPick.manuallyPack') }} {{ manualTarget.item?.name }}</div>
        <BaseFormField :label="$t('projectPick.enterItemId')" :error="manualError || ''">
          <BaseInput
            v-model="manualId"
            :placeholder="$t('projectPick.assetTagSnId')"
            autofocus
            @keydown.enter="confirmManual"
          />
        </BaseFormField>
        <div class="flex gap-2 mt-4">
          <button class="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border-0 bg-gray-700 text-gray-300" @click="manualTarget = null; manualId = ''">{{ $t('common.cancel') }}</button>
          <button class="flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer border-0 bg-emerald-900 text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed" :disabled="!manualId.trim()" @click="confirmManual">{{ $t('common.confirm') }}</button>
        </div>
      </div>
    </div>

    <!-- Quantity modal (Type 3) -->
    <QuantityModal
      :item="quantityTarget"
      :initialValue="quantityTarget?.packed ?? false ? (quantityTarget.packed_quantity ?? null) : null"
      @confirm="onQuantityConfirmed"
      @cancel="quantityTarget = null"
    />

    <!-- Unpack instance picker (Type 2 / unresolved) -->
    <UnpackModal
      :item="unpackTarget"
      :projectId="projectId"
      @confirm="handleUnpackConfirm"
      @cancel="unpackTarget = null; scanner?.focus()"
    />

    <!-- Unpack quantity modal (Type 3) -->
    <QuantityModal
      :item="unpackQtyTarget"
      mode="unpack"
      @confirm="handleUnpackQty"
      @cancel="unpackQtyTarget = null; scanner?.focus()"
    />

    <!-- Loading / error states -->
    <EmptyState v-if="loading" :message="$t('projectPick.loadingPickList')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <!-- Item lists -->
    <template v-else>
      <!-- Remaining -->
      <section v-if="remainingItems.length" class="px-4 py-2">
        <div class="section-label text-blue-400">
          {{ $t('projectPick.remainingLabel') }} ({{ remainingUnitCount }})
        </div>
        <template v-if="allSections.length">
          <template v-for="group in remainingGroups" :key="group.id ?? 'ungrouped'">
            <div class="section-label mt-3" :class="{ 'text-[var(--color-muted)]': group.id == null }">{{ group.name ?? $t('projectPick.ungrouped') }}</div>
            <PickListItem
              v-for="item in group.items"
              :key="item.id"
              :item="item"
              @quantity-tap="quantityTarget = $event"
              @manual-checkout="openManualCheckout"
              @unpack-one="unpackOne"
              @mark-missing="markMissing"
            />
          </template>
        </template>
        <template v-else>
          <PickListItem
            v-for="item in remainingItems"
            :key="item.id"
            :item="item"
            @quantity-tap="quantityTarget = $event"
            @manual-checkout="openManualCheckout"
            @unpack-one="unpackOne"
            @mark-missing="markMissing"
          />
        </template>
      </section>

      <!-- Packed -->
      <section v-if="packedItems.length" class="px-4 py-2">
        <div class="section-label text-green-400">
          {{ $t('projectPick.packedLabel') }} ({{ packedUnitCount }})
        </div>
        <template v-if="allSections.length">
          <template v-for="group in packedGroups" :key="group.id ?? 'ungrouped'">
            <div class="section-label mt-3" :class="{ 'text-[var(--color-muted)]': group.id == null }">{{ group.name ?? $t('projectPick.ungrouped') }}</div>
            <PickListItem
              v-for="item in group.items"
              :key="item.id"
              :item="item"
              @quantity-tap="quantityTarget = $event"
              @manual-checkout="openManualCheckout"
              @unpack-one="unpackOne"
            />
          </template>
        </template>
        <template v-else>
          <PickListItem
            v-for="item in packedItems"
            :key="item.id"
            :item="item"
            @quantity-tap="quantityTarget = $event"
            @manual-checkout="openManualCheckout"
            @unpack-one="unpackOne"
          />
        </template>
      </section>

      <!-- Returned -->
      <section v-if="returnedItems.length" class="px-4 py-2">
        <div class="section-label text-gray-500">
          {{ $t('projectPick.returnedLabel') }} ({{ returnedItems.length }})
        </div>
        <PickListItem
          v-for="item in returnedItems"
          :key="item.id"
          :item="item"
          @quantity-tap="quantityTarget = $event"
          @manual-checkout="openManualCheckout"
          @unpack-one="unpackOne"
        />
      </section>

      <!-- All done CTA -->
      <div v-if="progressPercent === 100" class="py-6 px-4 text-center">
        <button class="btn primary w-full max-w-sm" :disabled="checkingOut" @click="checkout">
          {{ checkingOut ? $t('common.saving') : (project?.status ?? 0) < 2 ? $t('projectPick.doneMarkReady') : $t('common.done') }}
        </button>
      </div>
    </template>

  </div>
</template>

<script setup>
import BaseTaskHelp from '@/components/base/BaseTaskHelp.vue'
import { useStore } from '@/stores/store'
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from '@/composables/useToast'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseToggle from '@/components/base/BaseToggle.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'
import PickListItem  from '@/components/picking/PickListItem.vue'
import QuantityModal from '@/components/picking/QuantityModal.vue'
import UnpackModal   from '@/components/picking/UnpackModal.vue'
import {
  getProjectPickList,
  packItem,
  updateConsumablePackedQuantity,
  packQtyItem,
  packConsumableItem,
  updateQtyItemPacked,
  addAdhocItem,
  addItemToProjectById,
  resolveItemMetadata,
  unpackProjectItem,
  getPackedCheckoutsForSlot,
  updateProject,
  getSearchResults,
  markItemMissing,
} from '@/api/api.js'
import { recordAdHocAdd, recordCorrection } from '@/composables/useOpsMetrics.js'

const store = useStore()
const route  = useRoute()
const router = useRouter()
const toast  = useToast()
const { t } = useI18n()

const projectId = computed(() => route.params.projectId)

// ── Data ─────────────────────────────────────────────────────────────────────
const project   = ref(null)
const pickList  = ref([])
const loading   = ref(false)
const loadError = ref(null)

async function loadPickList() {
  loading.value  = true
  loadError.value = null
  try {
    const res = await getProjectPickList(projectId.value)
    project.value  = res.project  ?? null
    pickList.value = res.pick_list ?? []
  } catch (e) {
    loadError.value = t('projectPick.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(loadPickList)

// ── Progress ──────────────────────────────────────────────────────────────────
// Count in units, not entries — a slot for 10 switches contributes 10 to the total.
function itemUnits(item) {
  return (item.item?.item_type === 3 || item.item?.item_type === 4 || item.unresolved) ? (item.planned_quantity ?? 1) : 1
}
function itemPackedUnits(item) {
  if (item.item?.item_type === 3 || item.item?.item_type === 4 || item.unresolved)
    return Math.min(item.packed_quantity ?? 0, item.planned_quantity ?? 1)
  return item.packed ? 1 : 0
}

const totalCount  = computed(() => pickList.value.reduce((sum, item) => sum + itemUnits(item), 0))
const packedCount = computed(() => pickList.value.reduce((sum, item) => sum + itemPackedUnits(item), 0))

const remainingUnitCount = computed(() =>
  remainingItems.value.reduce((sum, item) => sum + (itemUnits(item) - itemPackedUnits(item)), 0)
)
const packedUnitCount = computed(() =>
  packedItems.value.reduce((sum, item) => sum + itemPackedUnits(item), 0)
)

const progressPercent = computed(() =>
  totalCount.value ? Math.round((packedCount.value / totalCount.value) * 100) : 0
)

const progressCircumference = ref(2 * Math.PI * 34) // r=34
const progressOffset = computed(() =>
  progressCircumference.value * (1 - progressPercent.value / 100)
)

// ── Grouped lists ─────────────────────────────────────────────────────────────
function isItemPacked(item) {
  const t = item.item?.item_type
  if (t === 3 || t === 4)
    return (item.packed_quantity ?? 0) > 0 || !!item.packed
  if (item.unresolved)
    return !!item.packed || (item.packed_quantity ?? 0) >= (item.planned_quantity ?? 1)
  return !!item.packed
}

const remainingItems = computed(() =>
  pickList.value.filter(item => {
    if (item.missing) return false
    if (item.checkout_status === 'returned') return false
    return !isItemPacked(item)
  })
)

const packedItems = computed(() =>
  pickList.value.filter(item => isItemPacked(item))
)

const returnedItems = computed(() =>
  pickList.value.filter(item => item.checkout_status === 'returned')
)

// ── Section grouping ──────────────────────────────────────────────────────────
const allSections = computed(() => {
  const map = new Map()
  pickList.value.forEach(item => {
    if (item.section != null && !map.has(item.section)) {
      map.set(item.section, { id: item.section, name: item.section_name ?? t('ui.sectionNumber', { id: item.section }) })
    }
  })
  return [...map.values()].sort((a, b) => a.id - b.id)
})

function groupBySection(items) {
  const groups = []
  allSections.value.forEach(sec => {
    const secItems = items.filter(i => i.section === sec.id)
    if (secItems.length) groups.push({ id: sec.id, name: sec.name, items: secItems })
  })
  const ungrouped = items.filter(i => i.section == null)
  if (ungrouped.length) groups.push({ id: null, name: null, items: ungrouped })
  return groups
}

const remainingGroups = computed(() => groupBySection(remainingItems.value))
const packedGroups    = computed(() => groupBySection(packedItems.value))

// ── Scan ──────────────────────────────────────────────────────────────────────
const scanError   = ref(null)
const scanLoading = ref(false)
const scanner     = ref(null)
const ghostItem   = ref(null)   // barcode that wasn't on the list
const autoAdd     = ref(localStorage.getItem('pick_auto_add') === '1')
watch(autoAdd, val => localStorage.setItem('pick_auto_add', val ? '1' : '0'))
// ── Live search ─────────────────────────────────────────────────────────────
const liveResults  = ref([])
const liveLoading  = ref(false)
const liveAdding   = ref(false)

const mappedLiveResults = computed(() =>
  liveResults.value.map(r => ({
    id: String(r.id),
    label: r.displayName,
    sublabel: r.sub ?? '',
    badge: r.resultType === 'bundle' ? t('entity.bundle') : undefined,
    meta: r,
  }))
)

async function doLiveSearch(val) {
  if (!val || val.trim().length < 2) { liveResults.value = []; return }
  liveLoading.value = true
  try {
    const res = await getSearchResults(val.trim())
    const items = []
    ;(res.results?.item_metadata ?? []).forEach(r => {
      items.push({ id: r.object_id, resultType: 'item_metadata', displayName: r.display?.name ?? `#${r.object_id}`, sub: r.display?.subtitle || null })
    })
    ;(res.results?.bundle ?? []).forEach(r => {
      items.push({
        id: r.object_id,
        resultType: 'bundle',
        displayName: r.display?.name ?? t('ui.bundleNumber', { id: r.object_id }),
        sub: r.display?.subtitle || null,
      })
    })
    liveResults.value = items
  } catch {
    liveResults.value = []
  } finally {
    liveLoading.value = false
  }
}

async function addFromLive(result) {
  liveResults.value = []
  liveAdding.value  = true
  try {
    if (result.resultType === 'bundle') {
      store.addCheckoutBundle({ id: result.id, name: result.displayName })
      router.push({ path: '/checkout/', query: { project: projectId.value } })
    } else {
      // Check if this item already has an unpacked slot on the pick list
      const listMatch = pickList.value.find(p =>
        String(p.item_metadata_id) === String(result.id) && !isItemPacked(p)
      )
      if (listMatch) {
        await packItem(projectId.value, {
          item_metadata_id: listMatch.item_metadata_id,
          project_item_id: listMatch.id,
        })
        listMatch.packed_quantity = (listMatch.packed_quantity ?? 0) + 1
        if (listMatch.packed_quantity >= (listMatch.planned_quantity ?? 1)) listMatch.packed = true
        toast.success(t('ui.packedName', { name: listMatch.item?.name ?? result.displayName }))
        return
      }

      // Item is not on the pick list — respect the autoAdd toggle
      if (!autoAdd.value) {
        toast.warning(t('ui.notOnPickList', { name: result.displayName }))
        return
      }

      // autoAdd is on — add to project, pack, then reload for complete item data
      const res = await addItemToProjectById(projectId.value, result.id)
      const projectItemId = res?.id ?? res?.project_item_id
      if (projectItemId) {
        await packItem(projectId.value, {
          item_metadata_id: result.id,
          project_item_id: projectItemId,
        })
      }
      await loadPickList()
        toast.success(`${t('projectPick.addedAndPacked')} ${result.displayName}`)
    }
  } catch (e) {
    toast.error(e?.data?.error ?? e?.message ?? t('projectPick.addFailed'))
  } finally {
    liveAdding.value = false
    scanner.value?.focus()
  }
}
async function onScan(barcode) {
  liveResults.value = []
  scanError.value   = null
  scanLoading.value = true

  try {
    // ── 1. Exact match for Type 1 (Unique) resolved slots ────────────────────
    const uniqueMatch = pickList.value.find(item => {
      if (item.unresolved || item.item?.item_type !== 1) return false
      return (
        String(item.item_metadata_id) === barcode ||
        item.serial_number?.toLowerCase() === barcode.toLowerCase() ||
        item.asset_tag?.toLowerCase()     === barcode.toLowerCase()
      )
    })

    if (uniqueMatch) {
      if (uniqueMatch.packed) {
        scanError.value = t('ui.alreadyPackedName', { name: uniqueMatch.item?.name ?? barcode })
        return
      }
      await packItem(projectId.value, { item_metadata_id: uniqueMatch.item_metadata_id, project_item_id: uniqueMatch.id })
      uniqueMatch.packed_quantity = (uniqueMatch.packed_quantity ?? 0) + 1
      if (uniqueMatch.packed_quantity >= (uniqueMatch.planned_quantity ?? 1)) uniqueMatch.packed = true
      toast.success(t('ui.packedName', { name: uniqueMatch.item?.name ?? barcode }))
      return
    }

    // ── 1b. Exact match for Type 3 (Quantity) or Type 4 (Consumable) resolved slots ─────────
    const qtyMatch = pickList.value.find(item => {
      if (item.unresolved || (item.item?.item_type !== 3 && item.item?.item_type !== 4)) return false
      return (
        String(item.item_metadata_id) === barcode ||
        item.serial_number?.toLowerCase() === barcode.toLowerCase() ||
        item.asset_tag?.toLowerCase()     === barcode.toLowerCase()
      )
    })

    if (qtyMatch) {
      quantityTarget.value = qtyMatch
      return
    }

    // ── 2. Resolve the barcode to identify the item type ─────────────────────
    // Needed for Type 2 (resolved & unresolved) and Type 3 scan-to-pack
    const meta = await resolveItemMetadata(barcode)
    const scannedItemId = meta?.item?.id ?? (typeof meta?.item === 'number' ? meta.item : null)

    if (meta && scannedItemId != null) {
      // ── 2a. Resolved multi-item slot needing more of this type ──────────────
      const resolvedSlot = pickList.value.find(item =>
        !item.unresolved &&
        item.item?.item_type === 2 &&
        item.item_id === scannedItemId &&
        (item.packed_quantity ?? 0) < (item.planned_quantity ?? 1)
      )
      if (resolvedSlot) {
        await packItem(projectId.value, { item_metadata_id: meta.id, project_item_id: resolvedSlot.id })
        resolvedSlot.packed_quantity = (resolvedSlot.packed_quantity ?? 0) + 1
        if (resolvedSlot.packed_quantity >= (resolvedSlot.planned_quantity ?? 1)) resolvedSlot.packed = true
        toast.success(t('ui.packedProgress', { name: resolvedSlot.item?.name ?? barcode, packed: resolvedSlot.packed_quantity, planned: resolvedSlot.planned_quantity }))
        return
      }

      // ── 2b. Unresolved multi-item slot needing more of this type ────────────
      const unresolvedSlot = pickList.value.find(item =>
        item.unresolved &&
        item.item_id === scannedItemId &&
        (item.packed_quantity ?? 0) < (item.planned_quantity ?? 1)
      )
      if (unresolvedSlot) {
        await packItem(projectId.value, { item_metadata_id: meta.id, project_item_id: unresolvedSlot.id })
        unresolvedSlot.packed_quantity = (unresolvedSlot.packed_quantity ?? 0) + 1
        toast.success(t('ui.packedProgress', { name: unresolvedSlot.item?.name ?? barcode, packed: unresolvedSlot.packed_quantity, planned: unresolvedSlot.planned_quantity }))
        return
      }
    }

    if (autoAdd.value) {
      await addAdhoc(barcode)
    } else {
      ghostItem.value = barcode
    }
  } catch (e) {
    const raw = e?.data?.error ?? ''
    if (typeof raw === 'string' && raw.toLowerCase().includes('already checked out')) {
      scanError.value = t('ui.thisItemIsAlreadyCheckedOutOnAnotherActiveProject')
    } else {
      scanError.value = raw || t('ui.packFailedCheckConnection')
    }
  } finally {
    scanLoading.value = false
    scanner.value?.focus()
  }
}

// ── Quantity (Type 3) ─────────────────────────────────────────────────────────
const quantityTarget = ref(null)

// ── Unpack instance picker ────────────────────────────────────────────────────
const unpackTarget = ref(null)

// ── Unpack quantity (Type 3) ──────────────────────────────────────────────────
const unpackQtyTarget = ref(null)

async function onQuantityConfirmed({ item, quantity }) {
  quantityTarget.value = null
  const li     = pickList.value.find(x => x.id === item.id)
  const oldQty = li?.packed_quantity ?? 0
  if (quantity === oldQty) {
    toast.success(t('ui.quantityUnchanged', { count: quantity, name: item.item?.name }))
    scanner.value?.focus()
    return
  }
  try {
    if (item.item?.item_type === 4) {
      // Type 4 (Consumable): like type 3 but uses quantity on checkout
      if (oldQty === 0 && quantity > 0) {
        await packConsumableItem(projectId.value, {
          project_item_id:  item.id,
          item_metadata_id: item.item_metadata_id,
          quantity,
        })
      } else if (quantity === 0) {
        await updateConsumablePackedQuantity(item.id, 0)
      } else {
        await updateConsumablePackedQuantity(item.id, quantity)
      }
    } else if (item.item?.item_type === 3) {
      if (oldQty === 0 && quantity > 0) {
        // First pack: ONE checkout + store count in project_item.notes
        await packQtyItem(projectId.value, {
          project_item_id:  item.id,
          item_metadata_id: item.item_metadata_id,
          quantity,
        })
      } else if (quantity === 0) {
        // Unpack completely: delete the single checkout + clear notes
        const checkouts = await getPackedCheckoutsForSlot(projectId.value, item)
        if (checkouts.length) await unpackProjectItem(checkouts[0].id)
        await updateQtyItemPacked(item.id, 0)
      } else {
        // Already packed — just update the stored count, no checkout changes
        await updateQtyItemPacked(item.id, quantity)
      }
    } else {
      // Type 1 / 2: replace checkouts if needed
      if (oldQty > 0) {
        const checkouts = await getPackedCheckoutsForSlot(projectId.value, item)
        await Promise.all(checkouts.map(co => unpackProjectItem(co.id)))
      }
      if (quantity > 0) {
        await packItem(projectId.value, { item_metadata_id: item.item_metadata_id, project_item_id: item.id, quantity })
      }
    }
    if (li) {
      li.packed_quantity = quantity
      li.packed = quantity > 0
    }
    toast.success(t(oldQty > 0 ? 'ui.updatedQuantity' : 'ui.packedQuantity', { count: quantity, name: item.item?.name }))
  } catch (e) {
    toast.error(e?.message ?? t('projectPick.updateQtyFailed'))
  }
  scanner.value?.focus()
}

// ── Manual checkout (Type 1/2) ────────────────────────────────────────────────
const manualTarget = ref(null)
const manualId     = ref('')
const manualError  = ref(null)

function openManualCheckout(item) {
  manualTarget.value = item
  manualId.value     = ''
  manualError.value  = null
}

async function markMissing(item) {
  try {
    await markItemMissing(projectId.value, { pick_list_item_id: item.id })
    const li = pickList.value.find(x => x.id === item.id)
    if (li) li.missing = true
    recordCorrection({ reason: 'missing' })
    toast.warning(t('ux.markedMissing'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('projectPick.couldNotAddItem'))
  }
}

async function confirmManual() {
  manualError.value = null
  const id = manualId.value.trim()
  if (!id) return
  try {
    if (manualTarget.value.unresolved) {
      // Unresolved slot: resolve the entered ID to a specific item_metadata
      const meta = await resolveItemMetadata(id)
      if (!meta) { manualError.value = t('ui.itemNotFound'); return }
      if (meta.item?.id !== manualTarget.value.item_id) {
        manualError.value = t('ui.wrongItem', { actual: meta.item?.name, expected: manualTarget.value.item?.name })
        return
      }
      await packItem(projectId.value, { item_metadata_id: meta.id, project_item_id: manualTarget.value.id })
      const li = pickList.value.find(x => x.id === manualTarget.value.id)
      if (li) li.packed_quantity = (li.packed_quantity ?? 0) + 1
    } else {
      await packItem(projectId.value, { item_metadata_id: manualTarget.value.item_metadata_id, project_item_id: manualTarget.value.id })
      const li = pickList.value.find(x => x.id === manualTarget.value.id)
      if (li) {
        li.packed_quantity = (li.packed_quantity ?? 0) + 1
        if (li.packed_quantity >= (li.planned_quantity ?? 1)) li.packed = true
      }
    }
    toast.success(t('ui.packedName', { name: manualTarget.value.item?.name ?? id }))
    manualTarget.value = null
    manualId.value     = ''
  } catch (e) {
    manualError.value = e?.data?.error ?? t('ui.notFoundInInventory')
  }
  scanner.value?.focus()
}

// ── Unpack one unit ───────────────────────────────────────────────────────────
async function unpackOne(item) {
  const t = item.item?.item_type
  if (t === 1) {
    // Unique: only ever one checkout — unpack directly without modal
    try {
      const checkouts = await getPackedCheckoutsForSlot(projectId.value, item)
      if (!checkouts.length) { toast.error(t('projectPick.noActiveCheckout')); return }
      await unpackProjectItem(checkouts[0].id)
      const li = pickList.value.find(x => x.id === item.id)
      if (li) li.packed = false
      toast.success(`${t('projectPick.unpacked')} ${item.item?.name}`)
    } catch (e) {
      toast.error(e?.message ?? t('projectPick.unpackFailed'))
    }
    scanner.value?.focus()
  } else if (t === 3 || t === 4) {
    // Quantity / Consumable: open the standard qty modal (update to new total; 0 = full unpack)
    quantityTarget.value = item
  } else {
    // Type 2 / unresolved: show instance picker
    unpackTarget.value = item
  }
}

async function handleUnpackConfirm({ checkoutId, item }) {
  unpackTarget.value = null
  try {
    await unpackProjectItem(checkoutId)
    const li = pickList.value.find(x => x.id === item.id)
    if (li) {
      li.packed_quantity = Math.max(0, (li.packed_quantity ?? 0) - 1)
      li.packed = li.packed_quantity >= (li.planned_quantity ?? 1)
    }
    toast.success(`${t('projectPick.removedUnit')} ${item.item?.name}`)
  } catch (e) {
    toast.error(e?.message ?? t('projectPick.unpackFailed'))
  }
  scanner.value?.focus()
}

async function handleUnpackQty({ item, quantity }) {
  unpackQtyTarget.value = null
  try {
    const checkouts = await getPackedCheckoutsForSlot(projectId.value, item)
    const toRemove = checkouts.slice(0, quantity)
    if (!toRemove.length) { toast.error(t('projectPick.noPackedUnits')); return }
    await Promise.all(toRemove.map(co => unpackProjectItem(co.id)))
    const li = pickList.value.find(x => x.id === item.id)
    if (li) {
      li.packed_quantity = Math.max(0, (li.packed_quantity ?? 0) - toRemove.length)
      li.packed = li.packed_quantity >= (li.planned_quantity ?? 1)
    }
    toast.success(t('ui.unpackedQuantity', { count: toRemove.length, name: item.item?.name }))
  } catch (e) {
    toast.error(e?.message ?? t('projectPick.unpackFailed'))
  }
  scanner.value?.focus()
}

// ── Ghost / ad-hoc addition ───────────────────────────────────────────────────
async function addAdhoc(barcodeOverride = null) {
  const barcode = barcodeOverride ?? ghostItem.value
  ghostItem.value = null
  try {
    const res = await addAdhocItem(projectId.value, { barcode })
    const newItem = res?.pick_list_item
    if (newItem) {
      recordAdHocAdd({ projectId: projectId.value })
      pickList.value.push(newItem)
      // Also check out the item immediately
      if (newItem.id && newItem.item_metadata_id) {
        try {
          await packItem(projectId.value, {
            item_metadata_id: newItem.item_metadata_id,
            project_item_id: newItem.id,
          })
          const li = pickList.value.find(x => x.id === newItem.id)
          if (li) { li.packed = true; li.packed_quantity = 1 }
        } catch (packErr) {
          toast.warning(t('ui.addedButCheckoutFailed', { error: packErr?.data?.error ?? t('ui.checkoutError') }))
          scanner.value?.focus()
          return
        }
      }
      toast.success(`${t('projectPick.addedAndCheckedOut')} ${newItem.item?.name ?? barcode}`)
    }
  } catch (e) {
    toast.error(e?.data?.error ?? e?.message ?? t('projectPick.couldNotAddItem'))
  }
  scanner.value?.focus()
}

// ── Checkout ──────────────────────────────────────────────────────────────────
const checkingOut = ref(false)
const confirmingProject = ref(false)

async function confirmProject() {
  if (confirmingProject.value) return
  confirmingProject.value = true
  try {
    await updateProject(projectId.value, { status: 1 })
    project.value = { ...project.value, status: 1 }
    toast.success(t('ui.projectConfirmed'))
  } catch (e) {
    toast.error(e?.data?.detail ?? t('ui.couldNotConfirmProject'))
  } finally {
    confirmingProject.value = false
  }
}

async function checkout() {
  if (checkingOut.value) return
  // Packing is done — advance to Ready if the project isn’t already there or further along
  const currentStatus = project.value?.status ?? 0
  const shouldAdvance = currentStatus < 2
  if (!shouldAdvance) {
    router.push(`/project/${projectId.value}`)
    return
  }
  checkingOut.value = true
  try {
    await updateProject(projectId.value, { status: 2 })
    toast.success(t('ui.projectMarkedAsReady'))
  } catch (e) {
    toast.error(e?.data?.detail ?? t('ui.couldNotUpdateProjectStatus'))
  } finally {
    checkingOut.value = false
    router.push(`/project/${projectId.value}`)
  }
}
</script>
